import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMutation } from './mutation.js';
import type { Langue, TypeContact } from '@artosera/shared';
import { api, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee } from './cles.js';
import type { Pagination } from './types.js';
export interface RegleNewsletter {
  typesContact: TypeContact[];
  etiquettes: string[];
  langues: Langue[];
}
export interface ListeNewsletter {
  id: string;
  nom: string;
  description: string;
  regle: RegleNewsletter;
  synchronisee: boolean;
  membres: number;
  archive: boolean;
  creeLe: string;
  modifieLe: string;
}
export type BlocNewsletter =
  | { type: 'texte'; texte: string }
  | { type: 'oeuvre'; oeuvreId: string; lien?: string | null }
  | { type: 'exposition'; expositionId: string; lien?: string | null };
export interface StatistiquesNewsletter {
  envoyes: number;
  delivres: number;
  ouvertures: number;
  clics: number;
  desinscriptions: number;
  rebonds: number;
  luesLe: string;
}
export interface CampagneNewsletter {
  id: string;
  titre: string;
  sujet: string;
  langue: Langue;
  listes: string[];
  blocs: BlocNewsletter[];
  etat: 'brouillon' | 'programmee' | 'envoyee' | 'echouee';
  envoiDemandeLe: string | null;
  envoyeeLe: string | null;
  statistiques: StatistiquesNewsletter | null;
  erreur: string | null;
  revision: number;
  creeLe: string;
  modifieLe: string;
}
export interface VisuelNewsletter {
  id: string;
  cible: { type: 'oeuvre' | 'exposition'; id: string };
  format: 'carre' | 'vertical';
  etat: 'en_attente' | 'pret' | 'echoue';
  octets: number | null;
  erreur: string | null;
  creeLe: string;
}
export interface ReglagesNewsletter {
  connecteur: {
    configure: boolean;
    mode: 'simule' | 'reel';
    expediteur: { nom: string; email: string } | null;
  };
  webhook: { branche: boolean; brancheLe: string | null };
  derniereSynchro: { le: string; poussees: number; retirees: number; desinscrits: number } | null;
}
export type CorpsListe = Pick<ListeNewsletter, 'nom' | 'description' | 'regle'>;
export type CorpsCampagne = Pick<CampagneNewsletter, 'titre' | 'sujet'> &
  Partial<Pick<CampagneNewsletter, 'langue' | 'listes' | 'blocs'>>;
const path = (g: string, suffixe: string) => `/g/${encodeURIComponent(g)}/newsletter/${suffixe}`;
const id = encodeURIComponent;
const lecture = <R>(g: string, suffixe: string, a?: AppelApi) =>
  api.get<R>(path(g, suffixe), { galerie: g, ...a });
const post = <R>(g: string, suffixe: string, corps: unknown, a?: AppelApi) =>
  api.post<R>(path(g, suffixe), corps, { galerie: g, ...a });
export const newsletterApi = {
  listes: (g: string, a?: AppelApi) => lecture<{ listes: ListeNewsletter[] }>(g, 'listes', a),
  liste: (g: string, l: string, a?: AppelApi) =>
    lecture<{ liste: ListeNewsletter }>(g, `listes/${id(l)}`, a),
  creerListe: (g: string, v: CorpsListe, a?: AppelApi) =>
    post<{ liste: ListeNewsletter }>(g, 'listes', v, a),
  modifierListe: (g: string, l: string, v: Partial<CorpsListe>, a?: AppelApi) =>
    api.patch<{ liste: ListeNewsletter }>(path(g, `listes/${id(l)}`), v, { galerie: g, ...a }),
  archiverListe: (g: string, l: string, a?: AppelApi) =>
    post<{ liste: ListeNewsletter }>(g, `listes/${id(l)}/archiver`, {}, a),
  membres: (g: string, l: string, a?: AppelApi) =>
    lecture<{ membres: { id: string; nom: string; email: string }[]; pagination: Pagination }>(
      g,
      `listes/${id(l)}/membres`,
      a,
    ),
  campagnes: (g: string, a?: AppelApi) =>
    lecture<{ campagnes: CampagneNewsletter[]; pagination: Pagination }>(g, 'campagnes', a),
  campagne: (g: string, c: string, a?: AppelApi) =>
    lecture<{ campagne: CampagneNewsletter }>(g, `campagnes/${id(c)}`, a),
  creerCampagne: (g: string, v: CorpsCampagne, a?: AppelApi) =>
    post<{ campagne: CampagneNewsletter }>(g, 'campagnes', v, a),
  modifierCampagne: (g: string, c: string, v: CorpsCampagne & { revision: number }, a?: AppelApi) =>
    api.patch<{ campagne: CampagneNewsletter }>(path(g, `campagnes/${id(c)}`), v, {
      galerie: g,
      ...a,
    }),
  envoyer: (g: string, c: string, a?: AppelApi) =>
    post<{ campagne: CampagneNewsletter }>(g, `campagnes/${id(c)}/envoyer`, {}, a),
  apercu: (g: string, c: string, a?: AppelApi) =>
    lecture<{ html: string }>(g, `campagnes/${id(c)}/apercu`, a),
  rafraichirStatistiques: (g: string, c: string, a?: AppelApi) =>
    post(g, `campagnes/${id(c)}/statistiques`, {}, a),
  visuels: (g: string, a?: AppelApi) => lecture<{ visuels: VisuelNewsletter[] }>(g, 'visuels', a),
  creerVisuel: (g: string, v: Pick<VisuelNewsletter, 'cible' | 'format'>, a?: AppelApi) =>
    post<{ visuel: VisuelNewsletter }>(g, 'visuels', v, a),
  telechargerVisuel: (g: string, v: string, a?: AppelApi) =>
    lecture<{ telechargement: { url: string; expireLe: string } }>(
      g,
      `visuels/${id(v)}/telechargement`,
      a,
    ),
  reglages: (g: string, a?: AppelApi) =>
    lecture<{ reglages: ReglagesNewsletter }>(g, 'reglages', a),
  brancherWebhook: (g: string, a?: AppelApi) => post(g, 'webhook', {}, a),
  synchroniser: (g: string, a?: AppelApi) => post(g, 'synchroniser', {}, a),
};
function useMutationNewsletter<V, R>(g: string, mutationFn: (v: V, a: AppelApi) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn,
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: cleMetier(g, 'newsletter') });
      },
    }),
  );
}
export const useListesNewsletter = (g: string) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'listes'),
    queryFn: (c) => newsletterApi.listes(g, appelDeRequete(c)),
  });
export const useCampagnes = (g: string) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'campagnes'),
    queryFn: (c) => newsletterApi.campagnes(g, appelDeRequete(c)),
  });
export const useCampagne = (g: string, c: string) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'campagne', c),
    queryFn: (ctx) => newsletterApi.campagne(g, c, appelDeRequete(ctx)),
    enabled: Boolean(c),
  });
export const useApercuCampagne = (g: string, c: string, ouvert = true) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'apercu', c),
    queryFn: (ctx) => newsletterApi.apercu(g, c, appelDeRequete(ctx)),
    enabled: Boolean(c) && ouvert,
  });
export const useReglagesNewsletter = (g: string) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'reglages'),
    queryFn: (c) => newsletterApi.reglages(g, appelDeRequete(c)),
  });
export const useVisuels = (g: string) =>
  useQuery({
    queryKey: cleMetier(g, 'newsletter', 'visuels'),
    queryFn: (c) => newsletterApi.visuels(g, appelDeRequete(c)),
    refetchInterval: (q) =>
      q.state.data?.visuels.some((v) => v.etat === 'en_attente') ? 2000 : false,
  });
export const useCreerListe = (g: string) =>
  useMutationNewsletter(g, (v: CorpsListe, a) => newsletterApi.creerListe(g, v, a));
export const useModifierListe = (g: string) =>
  useMutationNewsletter(g, (v: { id: string; corps: Partial<CorpsListe> }, a) =>
    newsletterApi.modifierListe(g, v.id, v.corps, a),
  );
export const useArchiverListe = (g: string) =>
  useMutationNewsletter(g, (v: string, a) => newsletterApi.archiverListe(g, v, a));
export const useCreerCampagne = (g: string) =>
  useMutationNewsletter(g, (v: CorpsCampagne, a) => newsletterApi.creerCampagne(g, v, a));
export const useModifierCampagne = (g: string) =>
  useMutationNewsletter(g, (v: { id: string; corps: CorpsCampagne & { revision: number } }, a) =>
    newsletterApi.modifierCampagne(g, v.id, v.corps, a),
  );
export const useEnvoyerCampagne = (g: string) =>
  useMutationNewsletter(g, (v: string, a) => newsletterApi.envoyer(g, v, a));
export const useRafraichirStatistiques = (g: string) =>
  useMutationNewsletter(g, (v: string, a) => newsletterApi.rafraichirStatistiques(g, v, a));
export const useCreerVisuel = (g: string) =>
  useMutationNewsletter(g, (v: Pick<VisuelNewsletter, 'cible' | 'format'>, a) =>
    newsletterApi.creerVisuel(g, v, a),
  );
export const useTelechargerVisuel = (g: string) =>
  useMutationNewsletter(g, (v: string, a) => newsletterApi.telechargerVisuel(g, v, a));
export const useBrancherWebhook = (g: string) =>
  useMutationNewsletter(g, (_v: void, a) => newsletterApi.brancherWebhook(g, a));
export const useSynchroniserNewsletter = (g: string) =>
  useMutationNewsletter(g, (_v: void, a) => newsletterApi.synchroniser(g, a));
